import {
  BodyTypeSelect,
  DriveTypeSelect,
  FuelTypeSelect,
  SharedForm,
  TransmissionSelect,
} from '@components';
import { L } from '@lib';
import { InitialVehicle } from '@services';
import { useStore } from '@stores';
import { Rules } from '@utils';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, FormInstance, Input, Row } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';

interface IVehicleFormProps {
  form: FormInstance<any>;
  disabled?: boolean;
  searchDisabled?: boolean;
  regNumber: string;
  showAdditionalFields: boolean;
  setRegNumber: (value: string) => void;
  handleVehicleSearch: (value: string) => void;
  onFinish: (values: any) => Promise<void>;
  onCancel: (value: boolean) => void;
}

export const VehicleSearchForm: React.FC<IVehicleFormProps> = observer(
  ({
    form,
    regNumber,
    disabled = false,
    searchDisabled = false,
    showAdditionalFields,
    setRegNumber,
    onFinish,
    onCancel,
    handleVehicleSearch,
  }) => {
    const { vehicleStore } = useStore();
    const { loading } = vehicleStore;

    const handleSearch = async (e: any) => {
      e.preventDefault();

      if (searchDisabled || regNumber.trim().length === 0) {
        return;
      }

      await handleVehicleSearch(regNumber);
    };

    const searchRow = (
      <Row gutter={8} align="middle" wrap={false}>
        <Col flex="auto">
          <Input
            value={regNumber}
            placeholder={L('Enter reg number')}
            disabled={searchDisabled}
            onChange={(e) => setRegNumber(e.currentTarget.value.toUpperCase())}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col flex="none">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={loading}
            disabled={loading || searchDisabled}
            onClick={handleSearch}
            style={{ minWidth: 110 }}
          >
            {L('Search')}
          </Button>
        </Col>
      </Row>
    );

    if (!showAdditionalFields) {
      return searchRow;
    }

    return (
      <SharedForm
        form={form}
        name="createOrUpdateVehicle"
        layout="vertical"
        submitTitle={L('Save new vehicle')}
        initialValues={InitialVehicle}
        onFinish={onFinish}
        onCancel={onCancel}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <Form.Item
          name="registrationNumber"
          label={L('Registration number')}
          rules={Rules.required}
          style={{ marginBottom: 8 }}
        >
          {searchRow}
          <p style={{ display: 'block', marginTop: 8 }}>
            {L('Vehicle is not found, please add it manually.')}
          </p>
        </Form.Item>
        {showAdditionalFields && (
          <>
            <Row gutter={8} justify="space-between">
              <Col span={8}>
                <Form.Item
                  rules={!disabled ? Rules.required : undefined}
                  name="combinedMake"
                  label={L('Make')}
                >
                  <Input placeholder={L('Make')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  rules={!disabled ? Rules.required : undefined}
                  name="combinedModel"
                  label={L('Model')}
                >
                  <Input placeholder={L('Model')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="combinedVin"
                  label={L('Vin')}
                  rules={
                    !disabled
                      ? [
                          { required: true, message: 'VIN is required' },
                          {
                            pattern: /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/,
                            message: 'Invalid VIN format',
                          },
                        ]
                      : undefined
                  }
                >
                  <Input
                    placeholder="VIN"
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      form.setFieldsValue({ combinedVin: value });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8} justify="space-between">
              <Col span={8}>
                <Form.Item label={L('Year of manufacture')} name="dvlaYearOfManufacture">
                  <Input type="number" min={0} placeholder={L('Year of manufacture')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={L('Engine size')} name="combinedEngineCapacity">
                  <Input type="number" min={0} placeholder={L('Engine size')} />
                </Form.Item>
              </Col>
              {form.getFieldValue('exemptFromMot') ? (
                <Col span={8}>
                  <Form.Item label={L('MOT expiry date')}>
                    <Input disabled placeholder={L('Select date')} value={L('Exempt from MOT')} />
                  </Form.Item>
                </Col>
              ) : (
                <Col span={8}>
                  <Form.Item name="motExpiryDate" label={L('MOT expiry date')}>
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Row gutter={8} justify="space-between">
              <Col span={8}>
                <Form.Item
                  name="combinedFuelType"
                  label={L('Fuel type')}
                  rules={!disabled ? Rules.required : undefined}
                >
                  <FuelTypeSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="combinedTransmission"
                  label={L('Gear type')}
                  rules={!disabled ? Rules.required : undefined}
                >
                  <TransmissionSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="vehicleCategoryDescription" label={L('Vehicle type')}>
                  <BodyTypeSelect />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8} justify="space-between">
              <Col span={8}>
                <Form.Item name="driveType" label={L('Drive type')}>
                  <DriveTypeSelect />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="colour" label={L('Colour')}>
                  <Input placeholder={L('Colour')} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="dateFirstRegistered" label={L('First registration')}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    showTime={false}
                    style={{ width: '100%' }}
                    placeholder="First registration"
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </SharedForm>
    );
  }
);
